
import React from "react";
import { BirdieGameGroup } from "@/types";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { Check, DollarSign } from "lucide-react";

interface GameCardProps {
  game: BirdieGameGroup;
  courseName: string;
  onClick: () => void;
}

const GameCard = ({ game, courseName, onClick }: GameCardProps) => {
  return (
    <Card 
      key={game.id} 
      className={cn(
        "hover:shadow-md transition-all cursor-pointer rounded-xl shadow-sm",
        game.isArchived && "opacity-70"
      )}
      onClick={onClick}
    >
      <CardHeader className="pb-2">
        <CardTitle className="font-display text-lg font-semibold flex items-center justify-between">
          <span>{game.name}</span>
          <div className="flex items-center gap-2">
            {game.isArchived && (
              <Badge variant="outline" className="text-muted-foreground border-border">
                Completed
              </Badge>
            )}
            <Badge>{game.year}</Badge>
          </div>
        </CardTitle>
        <p className="text-sm text-muted-foreground">{courseName}</p>
      </CardHeader>
      
      <CardContent className="pt-0">
        <div className="flex flex-col space-y-4">
          {/* Comma-separated list of golfers */}
          <div className="text-sm">
            <span className="text-muted-foreground">Golfers: </span>
            <span>
              {game.members && game.members.map((member, index) => {
                const isWinner = game.winner === member.id;
                
                return (
                  <span key={member.id}>
                    <span 
                      className={cn(
                        isWinner && "font-semibold text-amber-500"
                      )}
                    >
                      {member.name}
                      {isWinner && " 🏆"}
                    </span>
                    {index < (game.members?.length || 0) - 1 && ", "}
                  </span>
                );
              })}
            </span>
          </div>
          
          {game.bet && (
            <div className="flex items-center text-sm">
              <DollarSign className="h-4 w-4 mr-1 text-forest" />
              <span>{game.bet}</span>
              {game.isPaid && (
                <Badge variant="outline" className="ml-2 bg-forest/5 text-forest border-forest/20">
                  <Check className="h-3 w-3 mr-1" /> Paid
                </Badge>
              )}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default GameCard;
